/**
 * useNotifications — web notification list state. Wraps the shared
 * notificationApiService (same API the mobile NotificationContext uses)
 * without any FCM/push machinery.
 */
import { useCallback, useEffect, useRef, useState } from 'react';
import notificationApiService from '@wlb/shared/api/services/notificationApiService';
import groupService from '@wlb/shared/api/services/groupService';
import messageService from '@wlb/shared/api/services/messageService';
import { useProfile } from '@wlb/shared/context/ProfileContext';
import { ToastService } from '@wlb/shared/platform/notifier';
import { resolveProfilePictureUrl } from '@wlb/shared/utils/imageUrl';
import { resolveNotificationKind } from '@wlb/shared/utils/notificationHelpers';
import { INVITE_STATUS, isAlreadyHandledInvite } from '@wlb/shared/utils/buddyInvite';
import { notifyBuddyRelationshipChanged } from '@wlb/shared/utils/buddyRelationshipEvents';
import { formatContentTimestamp, getTimezoneOffsetMinutes } from '@wlb/shared/utils/dateHelpers';
import { useTranslation } from '@wlb/shared/i18n/useTranslation';
import { getApiErrorMessage } from '@wlb/shared/utils/apiErrorHandler';
import type { ApiError } from '@wlb/shared/types/api';
import type { NotificationItem } from '@wlb/shared/types/notification';
import {
  extractNotificationRows,
  getNotificationActorGuid,
  getNotificationActorImage,
  getGroupJoinRequestApiId,
  getNotificationActorName,
  getNotificationApiId,
  getNotificationRowApiId,
  getPositiveApiId,
  isResolvedGroupJoinRequestError,
  matchesGroupJoinRequest,
  NOTIFICATIONS_PAGE_SIZE,
  type NotificationRow,
} from './notificationRows';
import { resolveAvatarSrc } from '../messages/avatarSource';

/** Resolved actor identity, keyed by `conversationId:actorUserId`. */
export type NotificationActorIdentities = Map<string, { name: string; avatar: string | null }>;

const actorIdentityKey = (conversationId: unknown, actorUserId: unknown): string =>
  `${Number(conversationId)}:${Number(actorUserId)}`;

/**
 * Applies participant-resolved identities to rows whose payload carried no
 * actor name. Pure so the patching can be tested without the API.
 */
export const applyMessageActorIdentities = (
  rows: NotificationItem[],
  identities: NotificationActorIdentities,
): NotificationItem[] =>
  rows.map(row => {
    if (row.user?.name?.trim() || !row.conversationId) return row;
    const actorUserId = row.actorUserId ?? row.senderId;
    const identity =
      actorUserId != null
        ? identities.get(actorIdentityKey(row.conversationId, actorUserId))
        : undefined;
    if (!identity) return row;
    return {
      ...row,
      user: {
        ...row.user,
        name: identity.name,
        avatar: identity.avatar ? { uri: identity.avatar } : (row.user?.avatar ?? null),
      },
    };
  });

/** Max parallel participant lookups — same cap MessageContext hydrates with. */
const PARTICIPANT_LOOKUP_CONCURRENCY = 4;

/**
 * Message notifications carry only the message text — no actor name, and the
 * API's actorProfilePicture is the default placeholder on every row. The
 * conversation's participant list is always readable by its members, so the
 * actor's username/photo is resolved from there.
 *
 * Lookups run in small batches: one request per distinct conversation, but an
 * inbox full of message rows must not stampede the participants endpoint.
 */
export const resolveMessageActorIdentities = async (
  rows: NotificationItem[],
): Promise<NotificationActorIdentities> => {
  const identities: NotificationActorIdentities = new Map();
  const pending = rows.filter(
    row => !row.user?.name?.trim() && row.conversationId && (row.actorUserId ?? row.senderId),
  );
  if (pending.length === 0) return identities;

  const conversationIds = Array.from(new Set(pending.map(row => Number(row.conversationId))));

  for (let i = 0; i < conversationIds.length; i += PARTICIPANT_LOOKUP_CONCURRENCY) {
    const batch = conversationIds.slice(i, i + PARTICIPANT_LOOKUP_CONCURRENCY);
    await Promise.all(
      batch.map(async conversationId => {
        try {
          const members = await messageService.getParticipants(conversationId);
          members.forEach(member => {
            const memberUserId = getPositiveApiId(member.id);
            const name = member.username?.trim() || member.name?.trim() || '';
            if (memberUserId == null || !name) return;
            identities.set(actorIdentityKey(conversationId, memberUserId), {
              name,
              avatar: resolveAvatarSrc(member.avatar),
            });
          });
        } catch {
          // A participant lookup failure leaves the row anonymous, never drops it.
        }
      }),
    );
  }

  return identities;
};

const mapRawNotification = (item: NotificationRow): NotificationItem => {
  const notificationId = getNotificationRowApiId(item) ?? undefined;
  const actorProfilePicture = getNotificationActorImage(item);
  const avatarUrl = resolveProfilePictureUrl(actorProfilePicture);
  const actorUserId = getPositiveApiId(
    item.actorUserId,
    item.requestedByUserId,
    item.senderId,
  ) ?? undefined;
  const actorUserGuid = getNotificationActorGuid(item);
  const groupId = getPositiveApiId(item.groupId, item.GroupId) ?? undefined;
  const actorName = getNotificationActorName(item);

  return {
    ...item,
    id: notificationId
      ? String(notificationId)
      : item.id
        ? String(item.id)
        : Math.random().toString(),
    type: resolveNotificationKind({
      type: item.type,
      notificationTypeName: item.notificationTypeName,
      message: item.message,
      actorUserId,
      postId: item.postId,
      postCommentId: item.postCommentId,
      groupId,
      conversationId: item.conversationId,
      messageId: item.messageId,
    }),
    // The API carries the human-readable label in notificationTypeName.
    title: item.notificationTypeName || item.title || '',
    message: item.message || '',
    // Notification reads send `timezoneOffset`; the API answers in device time.
    time: formatContentTimestamp(item.createdAt || item.time || Date.now(), 'local'),
    section: item.section || 'Earlier',
    isRead: Boolean(item.isRead),
    user: {
      ...item.user,
      // A notification type describes the event, not the actor. Leaving an
      // unknown actor blank avoids rendering misleading initials such as F
      // for "Followee New Post".
      name: actorName,
      avatar: item.user?.avatar ?? (avatarUrl ? { uri: avatarUrl } : null),
    },
    senderId: actorUserId,
    notificationId,
    createdAt: item.createdAt,
    actorProfilePicture,
    actorUserId,
    actorUserGuid,
    notificationTypeName: item.notificationTypeName,
    postId: item.postId,
    postCommentId: item.postCommentId,
    groupId,
    groupJoinRequestId: getGroupJoinRequestApiId(item) ?? undefined,
    conversationId: item.conversationId,
    messageId: item.messageId,
  } as NotificationItem;
};

const removeResolvedGroupRequests = async (
  rows: NotificationItem[],
): Promise<NotificationItem[]> => {
  const requestsByGroup = new Map<number, NotificationItem[]>();
  rows.forEach(notification => {
    if (notification.type !== 'groupJoinRequest' || !notification.groupId) return;
    const groupRows = requestsByGroup.get(notification.groupId) ?? [];
    groupRows.push(notification);
    requestsByGroup.set(notification.groupId, groupRows);
  });

  const resolvedIds = new Set<string>();
  await Promise.all(
    Array.from(requestsByGroup.entries()).map(async ([groupId, notifications]) => {
      try {
        const result = await groupService.getGroupJoinRequests(String(groupId), 1, 100);
        // Do not declare a request stale when the pending list continues onto
        // another page and its matching row may simply be outside this batch.
        if (result.data.length < result.totalCount) return;
        notifications.forEach(notification => {
          if (!result.data.some(request => matchesGroupJoinRequest(notification, request))) {
            resolvedIds.add(notification.id);
          }
        });
      } catch {
        // A reconciliation failure must not hide a potentially valid request.
      }
    }),
  );

  if (resolvedIds.size === 0) return rows;

  await Promise.all(
    rows
      .filter(notification => resolvedIds.has(notification.id))
      .map(notification => {
        const notificationId = getNotificationApiId(notification);
        return notificationId == null
          ? Promise.resolve()
          : notificationApiService
              .deleteNotification({ notificationId })
              .then(() => undefined)
              .catch(() => undefined);
      }),
  );

  return rows.filter(notification => !resolvedIds.has(notification.id));
};

export interface UseNotificationsResult {
  notifications: NotificationItem[];
  loading: boolean;
  /** Set when the last load failed, so the page can offer a retry. */
  error: string | null;
  fetchNotifications: (isRefresh?: boolean) => Promise<void>;
  markAsRead: (id: string) => Promise<boolean>;
  markAllAsRead: () => Promise<boolean>;
  deleteNotification: (id: string) => Promise<boolean>;
  deleteNotifications: (ids: readonly string[]) => Promise<DeleteNotificationsResult>;
  deleteAllNotifications: () => Promise<boolean>;
  acceptBuddyRequest: (id: string, senderId?: number) => Promise<void>;
  rejectBuddyRequest: (id: string, senderId?: number) => Promise<void>;
  joinGroupRequest: (notification: NotificationItem) => Promise<boolean>;
}

export interface DeleteNotificationsResult {
  deletedIds: string[];
  failedIds: string[];
}

const DELETE_BATCH_SIZE = 5;

export function useNotifications(): UseNotificationsResult {
  const { t } = useTranslation();
  const { updateInvite } = useProfile();
  const [notifications, setNotifications] = useState<NotificationItem[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  /** Whether the list currently has rows, without re-running the fetch on every change. */
  const hasRowsRef = useRef(false);
  const requestRef = useRef<Promise<void> | null>(null);
  const pendingGroupReviewsRef = useRef(new Set<string>());

  const loadNotifications = useCallback(async (isRefresh: boolean) => {
    if (!isRefresh) setLoading(true);
    try {
      const response = await notificationApiService.getNotifications({
        startIndex: 1,
        pageSize: NOTIFICATIONS_PAGE_SIZE,
        timezoneOffset: getTimezoneOffsetMinutes(),
      });

      const rawRows = await removeResolvedGroupRequests(
        extractNotificationRows(response).map(mapRawNotification),
      );
      // Publish the rows as fetched — the list must not sit in its loading
      // state behind optional participant lookups.
      hasRowsRef.current = rawRows.length > 0;
      setNotifications(rawRows);
      setError(null);
      if (!isRefresh) setLoading(false);

      const identities = await resolveMessageActorIdentities(rawRows);
      if (identities.size > 0) {
        // Patch the live list rather than replacing it, so a mark-as-read the
        // member tapped while the lookups ran is not clobbered.
        setNotifications(current => applyMessageActorIdentities(current, identities));
      }
    } catch (err: any) {
      const message =
        err?.response?.data?.message || err?.message || t('common.errors.generic');
      setError(message);
      // With nothing on screen the page renders the failure itself; a toast is
      // only needed when the list the member is reading stays behind it.
      if (hasRowsRef.current) ToastService.show({ type: 'error', message });
    } finally {
      if (!isRefresh) setLoading(false);
    }
  }, [t]);

  // The list takes no parameters beyond the page the hook always asks for, so a
  // second caller arriving mid-flight — a remount replaying the mount effect, a
  // retry tapped while the first attempt is still open — waits on that request
  // instead of racing it.
  const fetchNotifications = useCallback(
    async (isRefresh = false): Promise<void> => {
      const pending = requestRef.current;
      if (pending) return pending;
      const request = loadNotifications(isRefresh).finally(() => {
        requestRef.current = null;
      });
      requestRef.current = request;
      return request;
    },
    [loadNotifications],
  );

  useEffect(() => {
    fetchNotifications().catch(() => undefined);
  }, [fetchNotifications]);

  const markAsRead = useCallback(async (id: string): Promise<boolean> => {
    const notif = notifications.find(n => n.id === id);
    if (!notif || notif.isRead) return true;

    // The API reads a missing notificationId as "mark every notification read",
    // so a row the payload never gave one for is left alone rather than marked
    // with an id derived from the display key.
    const numericId = Number(notif.notificationId);
    if (!Number.isFinite(numericId)) return true;

    try {
      await notificationApiService.updateReadStatus({ notificationId: numericId, isRead: true });
    } catch (err: any) {
      ToastService.show({
        type: 'error',
        message: err?.response?.data?.message || err?.message || t('common.errors.generic'),
      });
      return false;
    }

    setNotifications(prev => prev.map(n => (n.id === id ? { ...n, isRead: true } : n)));
    return true;
  }, [notifications, t]);

  const markAllAsRead = useCallback(async (): Promise<boolean> => {
    const hasUnread = notifications.some(n => !n.isRead);
    if (!hasUnread) return false;

    try {
      await notificationApiService.updateReadStatus({ isRead: true });
      setNotifications(prev => prev.map(n => ({ ...n, isRead: true })));
      return true;
    } catch (err: any) {
      ToastService.show({
        type: 'error',
        message: err?.response?.data?.message || err?.message || t('common.errors.generic'),
      });
      return false;
    }
  }, [notifications, t]);

  const deleteNotification = useCallback(async (id: string): Promise<boolean> => {
    const notification = notifications.find(item => item.id === id);
    if (!notification) return true;

    const notificationId = getNotificationApiId(notification);
    if (notificationId == null) {
      ToastService.show({ type: 'error', message: t('common.errors.generic') });
      return false;
    }

    try {
      await notificationApiService.deleteNotification({ notificationId });
    } catch (err: any) {
      ToastService.show({
        type: 'error',
        message: err?.response?.data?.message || err?.message || t('common.errors.generic'),
      });
      return false;
    }

    setNotifications(prev => prev.filter(item => item.id !== id));
    return true;
  }, [notifications, t]);

  const deleteNotifications = useCallback(async (
    ids: readonly string[],
  ): Promise<DeleteNotificationsResult> => {
    const uniqueIds = Array.from(new Set(ids));
    const targets = uniqueIds.map(id => {
      const notification = notifications.find(item => item.id === id);
      const notificationId = notification ? getNotificationApiId(notification) : null;
      return { id, notificationId };
    });
    const deletedIds: string[] = [];
    const failedIds: string[] = [];

    // The API has no batch endpoint. Keep a small concurrency cap so selecting
    // a large inbox does not issue every DELETE request at once.
    for (let index = 0; index < targets.length; index += DELETE_BATCH_SIZE) {
      const batch = targets.slice(index, index + DELETE_BATCH_SIZE);
      const results = await Promise.all(
        batch.map(async target => {
          if (target.notificationId == null) return { id: target.id, deleted: false };
          try {
            await notificationApiService.deleteNotification({
              notificationId: target.notificationId,
            });
            return { id: target.id, deleted: true };
          } catch {
            return { id: target.id, deleted: false };
          }
        }),
      );

      results.forEach(result => {
        (result.deleted ? deletedIds : failedIds).push(result.id);
      });
    }

    if (deletedIds.length > 0) {
      const deletedSet = new Set(deletedIds);
      setNotifications(prev => {
        const next = prev.filter(item => !deletedSet.has(item.id));
        hasRowsRef.current = next.length > 0;
        return next;
      });
    }

    if (failedIds.length > 0) {
      ToastService.show({
        type: 'error',
        message: t('home.notifications.errors.deleteSelectedFailed', {
          count: failedIds.length,
        }),
      });
    }

    return { deletedIds, failedIds };
  }, [notifications, t]);

  const deleteAllNotifications = useCallback(async (): Promise<boolean> => {
    if (notifications.length === 0) return false;

    try {
      await notificationApiService.deleteAllNotifications();
    } catch (err: any) {
      ToastService.show({
        type: 'error',
        message: err?.response?.data?.message || err?.message || t('common.errors.generic'),
      });
      return false;
    }

    hasRowsRef.current = false;
    setNotifications([]);
    return true;
  }, [notifications.length, t]);

  const respondToBuddyRequest = useCallback(
    async (id: string, senderId: number | undefined, accept: boolean): Promise<void> => {
      if (!senderId) {
        ToastService.show({
          type: 'error',
          message: t('common.errors.generic'),
        });
        return;
      }
      try {
        await updateInvite(senderId, accept ? INVITE_STATUS.accept : INVITE_STATUS.reject);
      } catch (err: any) {
        if (!isAlreadyHandledInvite(err)) {
          ToastService.show({
            type: 'error',
            message: err?.response?.data?.message || err?.message || t('common.errors.generic'),
          });
          return;
        }
      }
      setNotifications(prev =>
        prev.map(n =>
          n.id === id ? { ...n, isRead: true, status: accept ? 'accepted' : 'rejected' } : n,
        ),
      );
      notifyBuddyRelationshipChanged({
        relatedUserId: senderId,
        action: accept ? 'accept' : 'reject',
      });
      ToastService.show({
        type: 'success',
        message: t(accept ? 'home.notifications.toasts.buddyAccepted' : 'home.notifications.toasts.buddyRejected'),
      });
    },
    [updateInvite, t],
  );

  const acceptBuddyRequest = useCallback(
    (id: string, senderId?: number): Promise<void> => respondToBuddyRequest(id, senderId, true),
    [respondToBuddyRequest],
  );

  const rejectBuddyRequest = useCallback(
    (id: string, senderId?: number): Promise<void> => respondToBuddyRequest(id, senderId, false),
    [respondToBuddyRequest],
  );

  const removeGroupRequestNotification = useCallback(async (
    notification: NotificationItem,
  ): Promise<void> => {
    setNotifications(prev => prev.filter(item => item.id !== notification.id));

    const notificationId = getNotificationApiId(notification);
    if (notificationId == null) return;
    await notificationApiService
      .deleteNotification({ notificationId })
      .catch(() => undefined);
  }, []);

  const joinGroupRequest = useCallback(async (
    notification: NotificationItem,
  ): Promise<boolean> => {
    if (pendingGroupReviewsRef.current.has(notification.id)) return false;
    pendingGroupReviewsRef.current.add(notification.id);

    try {
      let joinRequestId = notification.groupJoinRequestId;

      if (!joinRequestId) {
        if (!notification.groupId) {
          throw new Error(t('home.notifications.errors.groupRequestUnavailable'));
        }

        const result = await groupService.getGroupJoinRequests(
          String(notification.groupId),
          1,
          100,
        );
        const request =
          result.data.find(candidate => matchesGroupJoinRequest(notification, candidate))
          ?? (result.data.length === 1 ? result.data[0] : undefined);

        if (!request) {
          await removeGroupRequestNotification(notification);
          return true;
        }
        joinRequestId = request.groupJoinRequestId;
      }

      await groupService.reviewJoinRequest(joinRequestId, 'Approve');
      await removeGroupRequestNotification(notification);
      ToastService.show({
        type: 'success',
        message: t('home.notifications.toasts.groupRequestApproved'),
      });
      return true;
    } catch (err: unknown) {
      if (isResolvedGroupJoinRequestError(err)) {
        await removeGroupRequestNotification(notification);
        return true;
      }
      ToastService.show({
        type: 'error',
        message:
          getApiErrorMessage(err as ApiError)
          || t('home.notifications.errors.groupRequestUnavailable'),
      });
      return false;
    } finally {
      pendingGroupReviewsRef.current.delete(notification.id);
    }
  }, [removeGroupRequestNotification, t]);

  return {
    notifications,
    loading,
    error,
    fetchNotifications,
    markAsRead,
    markAllAsRead,
    deleteNotification,
    deleteNotifications,
    deleteAllNotifications,
    acceptBuddyRequest,
    rejectBuddyRequest,
    joinGroupRequest,
  };
}
